import { existsSync } from "node:fs";
import { mkdir, readFile, readdir, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";
import { parseFrontmatter, validateDataRelativePath, exclusiveCreateFile } from "@vortex-os/core";
import {
  type DeclinedEntry,
  recordAcceptance,
  recordDecline,
} from "./decline-store.js";
import { writeDocAction } from "./doc-writer.js";
import { computeFingerprint } from "./fingerprint.js";
import type { ExpectedShape, LLMJudge } from "./llm-judge.js";
import type {
  AcceptResult,
  Proposal,
  ProposalAction,
  Proposer,
  ProposerContext,
} from "./proposer.js";

/**
 * In-session insight capture surface.
 *
 * Trigger logic (6 steps — design.md `Surface ①` for the full rationale):
 *
 *  1. **Token-count safety net.** Below `minTokensAccumulated`, return null.
 *  2. **LLM capture decision.** Ask the LLM whether the recent turns contain
 *     a worth-capturing insight; if yes, get a 3-5 sentence summary plus a
 *     1-3 word topic.
 *  3. **Topic-tree scan.** Walk `data/` excluding reserved system-meta
 *     directories. The result feeds the next LLM call as the placement
 *     context — *which existing topic folders does this insight relate to,
 *     and which files already discuss the topic?*
 *  4. **LLM placement decision.** With the summary, topic, and tree
 *     snapshot in hand, ask the LLM to pick one of four actions:
 *     `create-folder`, `create-file`, `append-section`, `update-file`.
 *     Preference order is wired into the prompt — extend before create.
 *  5. **Decline check.** Fingerprint the proposal (turn IDs + topic +
 *     action kind + target path) and short-circuit if the fingerprint is
 *     in the decline set.
 *  6. **Emit.** Return a `Proposal` whose `onAccept` applies the action
 *     and whose `onDecline` records the decline.
 *
 * `evaluate()` makes two LLM round-trips for a successful proposal and
 * exactly one for a *"no, nothing to capture right now"* outcome. The
 * second call is skipped when the first says no, which is the common case.
 */

const SYSTEM_META_DIRS = new Set([
  "worklog",
  "decision-log",
  "runbooks",
  "hubs",
  "_memory",
  "_templates",
  "_proactive-curator",
]);

export interface InsightProposerOptions {
  /** Below this token count the proposer never asks the LLM. */
  readonly minTokensAccumulated: number;
  /** Below this turn count the proposer never asks the LLM. */
  readonly minTurnsBeforeFirstCheck: number;
  /**
   * Cap on how many topic folders to surface to the placement LLM call.
   * Large instance trees will exceed any practical LLM context budget;
   * truncation keeps the call tractable. Default 100.
   */
  readonly maxTreeEntries?: number;
  /** Days after which a decline becomes eligible for re-evaluation. */
  readonly declineExpiryDays?: number;
}

export interface TurnRecord {
  readonly turnId: string;
  readonly role: "user" | "assistant" | "system";
  readonly content: string;
}

export interface InsightInput {
  readonly recentTurns: readonly TurnRecord[];
  readonly accumulatedTokens: number;
}

export interface InsightProposal extends Proposal {
  readonly kind: "capture-insight";
}

interface CaptureDecision {
  readonly verdict: "yes" | "no";
  readonly summary?: string;
  readonly topic?: string;
}

interface PlacementDecision {
  readonly actionKind: ProposalAction["kind"];
  readonly rationale: string;
  readonly folderPath?: string;
  readonly filename?: string;
  readonly filePath?: string;
  readonly sectionHeader?: string;
  readonly reason?: string;
}

interface TopicFile {
  readonly path: string;
  readonly filename: string;
  readonly frontmatterTopic?: string;
  readonly tags?: readonly string[];
}

interface TopicFolderSnapshot {
  readonly path: string;
  readonly files: readonly TopicFile[];
}

export interface TopicTreeSnapshot {
  readonly folders: readonly TopicFolderSnapshot[];
  /** True when the scan was truncated by `maxTreeEntries`. */
  readonly truncated: boolean;
}

export class InsightProposer implements Proposer<InsightInput, InsightProposal> {
  readonly kind = "capture-insight";

  constructor(private readonly options: InsightProposerOptions) {}

  async evaluate(
    input: InsightInput,
    ctx: ProposerContext,
  ): Promise<InsightProposal | null> {
    if (input.accumulatedTokens < this.options.minTokensAccumulated) return null;
    if (input.recentTurns.length < this.options.minTurnsBeforeFirstCheck) return null;

    const capture = await askCaptureDecision(ctx.llm, input.recentTurns);
    if (capture.verdict === "no" || !capture.summary || !capture.topic) return null;

    const tree = await scanTopicTree(
      ctx.cwd,
      this.options.maxTreeEntries ?? 100,
    );

    const placement = await askPlacementDecision(
      ctx.llm,
      capture.summary,
      capture.topic,
      tree,
    );
    if (!placement) return null;

    const action = buildAction(
      placement,
      capture.summary,
      capture.topic,
      ctx.now,
    );
    if (!action) return null;

    const targetPath = targetPathFromAction(action);
    const fingerprint = computeFingerprint({
      kind: "capture-insight",
      turnIds: input.recentTurns.map((t) => t.turnId),
      topic: capture.topic,
      actionKind: action.kind,
      targetPath,
    });
    if (ctx.declinedFingerprints.has(fingerprint)) return null;

    const sourceRefs = input.recentTurns.map((t) => t.turnId);
    const declineExpiryDays = this.options.declineExpiryDays;

    return {
      kind: "capture-insight",
      fingerprint,
      action,
      preview: capture.summary,
      rationale: placement.rationale,
      sourceRefs,
      onAccept: async () => {
        const writtenPath = await applyAction(ctx.cwd, action);
        await recordAcceptance(ctx.cwd, {
          kind: "capture-insight",
          fingerprint,
          topic: capture.topic!,
          actionKind: action.kind,
          writtenPath,
          now: ctx.now,
        });
        return {
          writtenPath,
          actionApplied: action.kind,
          nextActionHint: nextActionHintFor(action),
        } satisfies AcceptResult;
      },
      onDecline: async () => {
        await recordDecline(ctx.cwd, {
          kind: "capture-insight",
          fingerprint,
          topic: capture.topic!,
          actionKind: action.kind,
          targetPath,
          now: ctx.now,
          ...(declineExpiryDays !== undefined ? { expiryDays: declineExpiryDays } : {}),
        });
      },
    };
  }
}

async function askCaptureDecision(
  llm: LLMJudge,
  turns: readonly TurnRecord[],
): Promise<CaptureDecision> {
  const prompt = renderCapturePrompt(turns);
  const expected: ExpectedShape = {
    shape: "json",
    schema: {
      verdict: "yes|no",
      summary: "string (required when verdict=yes)",
      topic: "string (required when verdict=yes, 1-3 words)",
    },
  };
  const raw = await llm.ask(prompt, expected);
  return normalizeCaptureDecision(raw);
}

function renderCapturePrompt(turns: readonly TurnRecord[]): string {
  const transcript = turns
    .map((t) => `[${t.role}] ${t.content}`)
    .join("\n\n");
  return [
    "You are reviewing a conversation snippet to decide whether anything in it deserves to be captured as a standalone note.",
    "",
    "Capture only if the recent turns produced an insight, decision, or piece of structured thought that would be lost if the session ended right now. Routine status updates, command output, or trivial back-and-forth do not qualify.",
    "",
    "Return JSON:",
    `  { "verdict": "yes" | "no", "summary": "3-5 sentences if verdict=yes", "topic": "1-3 words if verdict=yes" }`,
    "",
    "Recent turns:",
    transcript,
  ].join("\n");
}

function normalizeCaptureDecision(raw: unknown): CaptureDecision {
  if (typeof raw !== "object" || raw === null) return { verdict: "no" };
  const obj = raw as Record<string, unknown>;
  if (obj.verdict !== "yes") return { verdict: "no" };
  const summary = typeof obj.summary === "string" ? obj.summary.trim() : "";
  const topic = typeof obj.topic === "string" ? obj.topic.trim() : "";
  if (!summary || !topic) return { verdict: "no" };
  return { verdict: "yes", summary, topic };
}

async function askPlacementDecision(
  llm: LLMJudge,
  summary: string,
  topic: string,
  tree: TopicTreeSnapshot,
): Promise<PlacementDecision | null> {
  const prompt = renderPlacementPrompt(summary, topic, tree);
  const expected: ExpectedShape = {
    shape: "json",
    schema: {
      actionKind: "create-folder|create-file|append-section|update-file",
      rationale: "string (one line)",
      folderPath: "string (create-folder, create-file)",
      filename: "string (create-folder, create-file)",
      filePath: "string (append-section, update-file)",
      sectionHeader: "string (append-section)",
      reason: "string (update-file)",
    },
  };
  const raw = await llm.ask(prompt, expected);
  return normalizePlacementDecision(raw);
}

function renderPlacementPrompt(
  summary: string,
  topic: string,
  tree: TopicTreeSnapshot,
): string {
  const treeText = tree.folders.length === 0
    ? "(no existing topic folders)"
    : tree.folders
        .map((f) => {
          const fileLines = f.files
            .map((file) => `    - ${file.filename}${file.frontmatterTopic ? ` [topic: ${file.frontmatterTopic}]` : ""}`)
            .join("\n");
          return `  ${f.path}/\n${fileLines}`;
        })
        .join("\n");
  const truncationNote = tree.truncated
    ? "\n\n(Tree snapshot truncated — additional folders exist but are omitted for brevity.)"
    : "";
  return [
    "You are deciding where to place a captured insight in the user's existing topic tree.",
    "",
    `Insight topic: ${topic}`,
    `Insight summary: ${summary}`,
    "",
    "Existing topic tree (data-relative paths, system-meta dirs excluded):",
    treeText + truncationNote,
    "",
    "Pick one placement action. Preference order — extend existing structure before adding new:",
    "  1. append-section  — an existing file is the right home; add a `## <sectionHeader>` section to it.",
    "  2. create-file     — a topic folder exists; add a new sibling file alongside its current contents.",
    "  3. create-folder   — the topic is genuinely new; create a folder and seed it with the first file.",
    "  4. update-file     — an existing file needs in-place revision (rare; prefer append).",
    "",
    "Return JSON:",
    `  { "actionKind": "append-section", "rationale": "one line why", "filePath": "<data-relative path>", "sectionHeader": "<section title without leading ##>" }`,
    `  { "actionKind": "create-file",    "rationale": "one line why", "folderPath": "<data-relative folder>", "filename": "<slug>.md" }`,
    `  { "actionKind": "create-folder",  "rationale": "one line why", "folderPath": "<data-relative folder>", "filename": "<slug>.md" }`,
    `  { "actionKind": "update-file",    "rationale": "one line why", "filePath": "<data-relative path>", "reason": "what needs revising" }`,
    "",
    "Paths must be relative to data/. Do not propose paths inside system-meta directories (worklog/, decision-log/, runbooks/, hubs/, _memory/, _templates/, _proactive-curator/, or any other _* directory).",
  ].join("\n");
}

function normalizePlacementDecision(raw: unknown): PlacementDecision | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const actionKind = String(obj.actionKind ?? "");
  const rationale = typeof obj.rationale === "string" ? obj.rationale.trim() : "";
  if (!rationale) return null;
  switch (actionKind) {
    case "create-folder":
    case "create-file": {
      const folderPath = typeof obj.folderPath === "string" ? obj.folderPath.trim() : "";
      const filename = typeof obj.filename === "string" ? obj.filename.trim() : "";
      if (!folderPath || !filename) return null;
      if (isSystemMetaPath(folderPath)) return null;
      return { actionKind: actionKind as "create-folder" | "create-file", rationale, folderPath, filename };
    }
    case "append-section": {
      const filePath = typeof obj.filePath === "string" ? obj.filePath.trim() : "";
      const sectionHeader = typeof obj.sectionHeader === "string" ? obj.sectionHeader.trim() : "";
      if (!filePath || !sectionHeader) return null;
      if (isSystemMetaPath(filePath)) return null;
      return { actionKind: "append-section", rationale, filePath, sectionHeader };
    }
    case "update-file": {
      const filePath = typeof obj.filePath === "string" ? obj.filePath.trim() : "";
      const reason = typeof obj.reason === "string" ? obj.reason.trim() : "";
      if (!filePath || !reason) return null;
      if (isSystemMetaPath(filePath)) return null;
      return { actionKind: "update-file", rationale, filePath, reason };
    }
    default:
      return null;
  }
}

function isSystemMetaPath(p: string): boolean {
  const normalized = p.replace(/\\/g, "/").replace(/^\/+/, "");
  const first = normalized.split("/")[0] ?? "";
  if (SYSTEM_META_DIRS.has(first)) return true;
  if (first.startsWith("_")) return true;
  return false;
}

function buildAction(
  placement: PlacementDecision,
  summary: string,
  topic: string,
  now: Date,
): ProposalAction | null {
  const body = renderCaptureBody(summary, topic, now);
  switch (placement.actionKind) {
    case "create-folder":
    case "create-file":
      if (!placement.folderPath || !placement.filename) return null;
      return {
        kind: placement.actionKind,
        folderPath: placement.folderPath,
        filename: placement.filename,
        body,
      };
    case "append-section":
      if (!placement.filePath || !placement.sectionHeader) return null;
      return {
        kind: "append-section",
        filePath: placement.filePath,
        sectionHeader: placement.sectionHeader,
        body: renderSectionBody(summary, now),
      };
    case "update-file":
      if (!placement.filePath || !placement.reason) return null;
      return {
        kind: "update-file",
        filePath: placement.filePath,
        body: renderUpdateBody(summary, topic, placement.reason, now),
        reason: placement.reason,
      };
  }
}

function renderCaptureBody(summary: string, topic: string, now: Date): string {
  const today = formatYmd(now);
  return `---
type: note
topic: ${topic}
created: ${today}
updated: ${today}
tags: [note, proactive-curator]
---

# ${topic}

> Captured by proactive-curator on ${today}. The user accepted this proposal; edit freely.

${summary}
`;
}

function renderSectionBody(summary: string, now: Date): string {
  const ymd = formatYmd(now);
  return `\n_Appended ${ymd} by proactive-curator._\n\n${summary}\n`;
}

function renderUpdateBody(
  summary: string,
  topic: string,
  reason: string,
  now: Date,
): string {
  const ymd = formatYmd(now);
  return `---
type: note
topic: ${topic}
updated: ${ymd}
---

# ${topic}

> Updated ${ymd} by proactive-curator. Reason: ${reason}

${summary}
`;
}

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function targetPathFromAction(action: ProposalAction): string {
  if (action.kind === "create-folder" || action.kind === "create-file") {
    return joinDataPath(action.folderPath, action.filename);
  }
  return joinDataPath(action.filePath);
}

function joinDataPath(...parts: string[]): string {
  return parts
    .filter((p) => p.length > 0)
    .map((p) => p.replace(/\\/g, "/").replace(/^\/+|\/+$/g, ""))
    .join("/");
}

async function applyAction(
  cwd: string,
  action: ProposalAction,
): Promise<string> {
  const dataDir = join(cwd, "data");
  switch (action.kind) {
    case "create-file": {
      // Route through the hardened shared write helper — path is validated
      // (traversal/absolute/drive/system-meta rejected) and the create is
      // exclusive (refuses to overwrite).
      const res = await writeDocAction(cwd, {
        kind: "create-file",
        targetRelPath: joinDataPath(action.folderPath, action.filename),
        body: action.body,
      });
      return res.writtenPath;
    }
    case "append-section": {
      // Hardened append: path validated, appends to an EXISTING file only.
      const res = await writeDocAction(cwd, {
        kind: "append-section",
        targetRelPath: joinDataPath(action.filePath),
        sectionHeader: action.sectionHeader,
        body: action.body,
      });
      return res.writtenPath;
    }
    case "create-folder": {
      // Not on the /curate v1 path, but still validate before any write so
      // the shared gate (not just the system-meta string check) governs it.
      // create-folder is a CREATE — make it exclusive so it never clobbers an
      // existing file, matching create-file. (update-file below is the sole
      // deliberate whole-file replace.)
      const rel = joinDataPath(action.folderPath, action.filename);
      const file = validateDataRelativePath(dataDir, rel);
      await mkdir(dirname(file), { recursive: true });
      await exclusiveCreateFile(file, action.body);
      return file;
    }
    case "update-file": {
      const rel = joinDataPath(action.filePath);
      const file = validateDataRelativePath(dataDir, rel);
      await mkdir(dirname(file), { recursive: true });
      await writeFile(file, action.body, "utf8");
      return file;
    }
  }
}

function nextActionHintFor(action: ProposalAction): string {
  if (action.kind === "append-section") {
    return `Section appended to ${action.filePath}. Open it to see the full thread of this topic.`;
  }
  if (action.kind === "update-file") {
    return `File updated at ${action.filePath}. The original content was replaced; check git history if you need to recover it.`;
  }
  return `New file at ${action.folderPath}/${action.filename}. Add cross-links from related docs as the topic grows.`;
}

async function scanTopicTree(
  cwd: string,
  maxEntries: number,
): Promise<TopicTreeSnapshot> {
  const dataDir = join(cwd, "data");
  if (!existsSync(dataDir)) {
    return { folders: [], truncated: false };
  }
  const folders: TopicFolderSnapshot[] = [];
  let counted = 0;
  let truncated = false;

  async function visit(absDir: string, relDir: string): Promise<void> {
    if (counted >= maxEntries) {
      truncated = true;
      return;
    }
    let entries;
    try {
      entries = await readdir(absDir, { withFileTypes: true });
    } catch {
      return;
    }
    const files: TopicFile[] = [];
    const subdirs: string[] = [];
    for (const e of entries) {
      if (e.isDirectory()) {
        if (isReservedDir(e.name, relDir === "")) continue;
        subdirs.push(e.name);
      } else if (e.isFile() && e.name.endsWith(".md")) {
        if (e.name === "README.md" || e.name === "_INDEX.md" || e.name === "MEMORY.md") continue;
        const filePath = join(absDir, e.name);
        let frontmatterTopic: string | undefined;
        let tags: readonly string[] | undefined;
        try {
          const raw = await readFile(filePath, "utf8");
          const parsed = parseFrontmatter<Record<string, unknown>>(raw);
          if (typeof parsed.frontmatter.topic === "string") {
            frontmatterTopic = parsed.frontmatter.topic;
          }
          if (Array.isArray(parsed.frontmatter.tags)) {
            tags = parsed.frontmatter.tags.filter((t): t is string => typeof t === "string");
          }
        } catch {
          // unreadable / no frontmatter — skip enrichment
        }
        files.push({
          path: joinDataPath(relDir, e.name),
          filename: e.name,
          ...(frontmatterTopic ? { frontmatterTopic } : {}),
          ...(tags ? { tags } : {}),
        });
      }
    }
    if (files.length > 0 || subdirs.length === 0) {
      // Record this folder if it has files of its own, or if it is a leaf
      // (subdir with neither files nor subfolders gets skipped naturally).
      if (relDir !== "") {
        folders.push({ path: relDir, files });
        counted += 1 + files.length;
      } else if (files.length > 0) {
        folders.push({ path: ".", files });
        counted += 1 + files.length;
      }
    }
    for (const d of subdirs) {
      if (counted >= maxEntries) {
        truncated = true;
        return;
      }
      const childRel = joinDataPath(relDir, d);
      await visit(join(absDir, d), childRel);
    }
  }

  await visit(dataDir, "");
  return { folders, truncated };
}

function isReservedDir(name: string, atRoot: boolean): boolean {
  if (atRoot && SYSTEM_META_DIRS.has(name)) return true;
  if (name.startsWith(".")) return true;
  if (atRoot && name.startsWith("_")) return true;
  return false;
}

// Re-export `DeclinedEntry` so consumers that only import from the
// insight-proposer module can introspect a decline entry without a
// separate `decline-store` import.
export type { DeclinedEntry };
